/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { Asset, AssetUtils, TimeUtils, TvAsset } from '@wit/vtv-x-foundation-lib';
import React from 'react';
import translate from 'services/i18n/translate';
import Badge from '../Badge';

interface Props {
  asset: TvAsset;
  onAssetClick: (asset: Asset) => void;
  ctxId: number | string;
}

export default function SmallScheduled({ onAssetClick, asset, ctxId }: Props): JSX.Element {
  const clicked = () => {
    onAssetClick(asset);
  };

  const hasValidSeasonNumber = asset && AssetUtils.hasValidSeasonNumber(asset);
  const hasValidEpisodeNumber = asset && AssetUtils.hasValidEpisodeNumber(asset);
  const assetTitle =
    asset?.episodeName || asset?.description || asset?.title || translate['programInfo.moreepisodes.noEpisodes'];

  const relativeDate = () => {
    const startDate = asset?.startDate;
    const startDateEpoch = startDate && TimeUtils.toEpoch(startDate);

    if (!startDateEpoch) {
      return '-';
    }

    if (TimeUtils.isToday(startDate)) {
      return `${asset?.startTime || ''} ${translate['date.today']}`;
    }

    if (TimeUtils.isTomorrow(startDate)) {
      return `${asset?.startTime || ''} ${translate['date.tomorrow']}`;
    }

    return `${asset?.startTime || ''} ${`${TimeUtils.getShortWeekDay(startDateEpoch)} ${TimeUtils.getDay(
      startDateEpoch
    )} ${TimeUtils.getShortMonth(startDateEpoch)}`}`;
  };

  return (
    <div
      onClick={clicked}
      className="dynamic-scheduled-card-container content-card-cell small scheduled"
      key={asset.key}
      id={`Card___${ctxId}-${asset.id}`}
    >
      <a className="context-switch content-card small scheduled" style={{ width: '100%' }} title={assetTitle}>
        <div className="content-card-info">
          <div className="content-card-info-inner">
            <p className="date" id={`Card_date___${ctxId}-${asset.id}`}>
              {relativeDate()}
            </p>
            {asset && !AssetUtils.isSpecialSeasonNumber(asset.seasonNumber) && (
              <p className="series-info">
                {asset && hasValidSeasonNumber && <span>{translate['programInfo.moreepisodes.seasonInitial']}</span>}
                {asset && hasValidSeasonNumber && (
                  <span className="season-number-label" id={`Card_season___${ctxId}-${asset.id}`}>
                    {asset.seasonNumber}
                  </span>
                )}
                {asset && hasValidSeasonNumber && hasValidEpisodeNumber && <span>, </span>}
                {asset && hasValidEpisodeNumber && <span>{translate['programInfo.moreepisodes.episodeInitial']}</span>}
                {asset && hasValidEpisodeNumber && (
                  <span className="season-number-label" id={`Card_episode___${ctxId}-${asset.id}`}>
                    {asset?.episodeNumber}
                  </span>
                )}
              </p>
            )}
            {assetTitle && <h3 id={`Card_title___${ctxId}-${asset.id}`}>{assetTitle}</h3>}
          </div>
          <div className="badge-wrapper">
            {asset.badges &&
              asset.badges.map((badge) => {
                return (
                  <Badge badge={badge} bemBlock="Card" bemModifierPrefix={`${ctxId}-${asset.id}`} key={badge.icon} />
                );
              })}
          </div>
        </div>
      </a>
    </div>
  );
}
